import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

/** One entry of a JSON:API error document. The atomic endpoint answers in this
 * shape, not Laravel's — `source.pointer` names the offending member. */
interface JsonApiError {
  detail?: string;
  title?: string;
  source?: { pointer?: string };
}

interface ServerErrorResponse {
  message?: string;
  /** Laravel keys its errors by field; JSON:API returns a list. */
  errors?: Record<string, string[]> | JsonApiError[];
}

const readResponse = (error: unknown) =>
  (error as { response?: { status?: number; data?: ServerErrorResponse } } | undefined)?.response;

/**
 * Turns a JSON:API pointer into the key the field map is written in.
 * `/atomic:operations/0/data/attributes/first_name` and
 * `/data/attributes/first_name` both become `data.attributes.first_name`, so one
 * map serves both the atomic and the plain endpoints.
 */
const pointerToKey = (pointer: string | undefined): string => {
  if (!pointer) {
    return "";
  }

  const fromData = pointer.slice(pointer.indexOf("/data/") + 1);

  return (fromData || pointer).replace(/^\/+/, "").replace(/\//g, ".");
};

/** Flattens either error shape into (key, message) pairs. */
const errorEntries = (errors: ServerErrorResponse["errors"]): [string, string][] => {
  if (!errors) {
    return [];
  }

  if (Array.isArray(errors)) {
    return errors
      .map((error): [string, string] => [
        pointerToKey(error.source?.pointer),
        error.detail ?? error.title ?? "",
      ])
      .filter(([, message]) => message !== "");
  }

  return Object.entries(errors)
    .map(([key, messages]): [string, string] => [key, messages?.[0] ?? ""])
    .filter(([, message]) => message !== "");
};

/**
 * Maps a validation response onto react-hook-form fields via `keyToField`, so
 * each error shows under its input. Both shapes are read: Laravel's
 * `{ errors: { "data.attributes.x": [...] } }` and JSON:API's
 * `{ errors: [{ detail, source: { pointer } }] }`. Messages are shown verbatim
 * (server is trusted).
 *
 * Returns `mapped` (count of messages bound to a field), `unmapped` (messages
 * whose keys don't map to a field) and `message` — the response's own message
 * when it has one, otherwise a line naming the HTTP status, so a failure with
 * an empty body still says something the reader can act on rather than
 * collapsing into a generic "failed".
 */
export interface AppliedServerErrors {
  mapped: number;
  unmapped: string[];
  message?: string;
  status?: number;
}

export const applyServerErrors = <TValues extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<TValues>,
  keyToField: Partial<Record<string, Path<TValues>>>,
): AppliedServerErrors => {
  const response = readResponse(error);
  const status = response?.status;
  // No response at all means the request never landed — a network or CORS
  // failure, which reads nothing like a validation error and shouldn't be
  // reported as one.
  const message =
    response?.data?.message ??
    (response ? undefined : ((error as { message?: string } | undefined)?.message ?? undefined));

  let mapped = 0;
  const unmapped: string[] = [];

  for (const [key, message] of errorEntries(response?.data?.errors)) {
    const field = keyToField[key];

    if (field) {
      setError(field, { type: "server", message });
      mapped += 1;
    } else {
      unmapped.push(message);
    }
  }

  return { mapped, unmapped, message, status };
};

/**
 * The one sentence worth showing for a failed request that has no form to bind
 * to — a table checkbox, a delete. The first validation message when there is
 * one (Laravel's own `message` only summarizes it, "… (and 1 more error)"),
 * then the response's message, then a network failure's. `undefined` when the
 * response says nothing, so the caller supplies its own fallback.
 */
export const serverErrorMessage = (error: unknown): string | undefined => {
  const response = readResponse(error);
  const [firstEntry] = errorEntries(response?.data?.errors);

  if (firstEntry) {
    return firstEntry[1];
  }

  if (response) {
    return response.data?.message || undefined;
  }

  return (error as { message?: string } | undefined)?.message || undefined;
};
